import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Star } from 'lucide-react';

interface B2BPricingProps {
  onGetStarted?: (tier?: string) => void;
}

export default function B2BPricingSection({ onGetStarted }: B2BPricingProps) {
  const [isAnnual, setIsAnnual] = useState(true);

  const handleGetStarted = (tier?: string) => {
    if (onGetStarted) {
      onGetStarted(tier);
    }
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal text-black mb-6">
            Protect Your Brand. Stay Ahead of Trends.
          </h2>
          <p className="font-sans text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            Smart social listening and crisis detection for growing brands. Monitor everything, miss nothing.
          </p>
        </div>

        {/* Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center bg-white rounded-xl p-1 shadow-sm">
            <button 
              onClick={() => setIsAnnual(true)}
              className={`text-sm font-medium px-4 py-2 rounded-xl transition-all ${
                isAnnual ? 'bg-black text-white shadow' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Annual (Save 25%)
            </button>
            <button 
              onClick={() => setIsAnnual(false)}
              className={`text-sm font-medium px-4 py-2 rounded-xl transition-all ${
                !isAnnual ? 'bg-black text-white shadow' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
          </div>
        </div>

        <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
          {/* Startup */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
            <h3 className="font-serif text-xl font-normal text-black mb-2">Startup</h3>
            <p className="font-sans text-gray-600 mb-6">Essential brand monitoring</p>
            <div className="mb-6">
              <span className="font-serif text-3xl font-normal text-black">
                ${isAnnual ? '49' : '65'}
              </span>
              <span className="font-sans text-gray-500">/month</span>
              {isAnnual && (
                <div className="text-sm text-green-600 font-medium">Save $192/year</div>
              )}
            </div>
            <ul className="font-sans text-sm text-gray-600 space-y-3 mb-8">
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Monitor 5 keywords/mentions
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                3 social platforms
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Basic sentiment analysis
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Daily reports
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Email alerts
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                1 team member
              </li>
            </ul>
            <Button 
              onClick={() => handleGetStarted('startup')}
              variant="outline" 
              className="w-full font-sans border-gray-300 hover:border-gray-400"
            >
              Start Free Trial
            </Button>
          </div>

          {/* Growth (Most Popular) */}
          <div className="bg-white border-2 border-black rounded-2xl p-8 shadow-lg relative transform scale-105">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs font-bold px-3 py-1 rounded-full">
              MOST POPULAR
            </div>
            <h3 className="font-serif text-xl font-normal text-black mb-2">Growth</h3>
            <p className="font-sans text-gray-600 mb-6">Advanced brand intelligence</p>
            <div className="mb-6">
              <span className="font-serif text-3xl font-normal text-black">
                ${isAnnual ? '149' : '199'}
              </span>
              <span className="font-sans text-gray-500">/month</span>
              {isAnnual && (
                <div className="text-sm text-green-600 font-medium">Save $600/year</div>
              )}
            </div>
            <ul className="font-sans text-sm text-gray-600 space-y-3 mb-8">
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Monitor 25 keywords/mentions
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                All major platforms
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Advanced sentiment & crisis detection
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Competitor analysis
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Real-time alerts & dashboards
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Viral prediction insights
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Up to 5 team members
              </li>
            </ul>
            <Button 
              onClick={() => handleGetStarted('growth')}
              className="w-full font-sans bg-black hover:bg-gray-800"
            >
              Start Free Trial
            </Button>
          </div>

          {/* Enterprise */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
            <div className="flex items-center mb-2">
              <h3 className="font-serif text-xl font-normal text-black">Enterprise</h3>
              <Star className="w-4 h-4 text-yellow-500 ml-2" />
            </div>
            <p className="font-sans text-gray-600 mb-6">Custom solutions for large brands</p>
            <div className="mb-6">
              <span className="font-serif text-2xl font-normal text-black">Custom</span>
              <div className="text-sm text-gray-500">Starting at $500/month</div>
            </div>
            <ul className="font-sans text-sm text-gray-600 space-y-3 mb-8">
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Unlimited monitoring
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Custom platform integrations
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Advanced analytics & reporting
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Dedicated account manager
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                API access & white labeling
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-3" />
                Unlimited team members
              </li>
            </ul>
            <Button 
              onClick={() => handleGetStarted('enterprise')}
              variant="outline" 
              className="w-full font-sans border-gray-300 hover:border-gray-400"
            >
              Contact Sales
            </Button>
          </div>
        </div>

        {/* Business Features */}
        <div className="mt-16 bg-white rounded-2xl p-8 border border-gray-200">
          <h3 className="font-serif text-2xl font-normal text-black mb-6 text-center">
            Business Intelligence Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-4 mx-auto">
                <span className="text-2xl">📊</span>
              </div>
              <h4 className="font-serif text-lg font-normal text-black mb-2">Brand Monitoring</h4>
              <p className="font-sans text-sm text-gray-600">
                Track brand mentions, sentiment, and engagement across all platforms
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-4 mx-auto">
                <span className="text-2xl">⚡</span>
              </div>
              <h4 className="font-serif text-lg font-normal text-black mb-2">Crisis Detection</h4>
              <p className="font-sans text-sm text-gray-600">
                Get instant alerts for potential reputation threats before they escalate
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-4 mx-auto">
                <span className="text-2xl">📈</span>
              </div>
              <h4 className="font-serif text-lg font-normal text-black mb-2">Trend Prediction</h4>
              <p className="font-sans text-sm text-gray-600">
                Identify viral content opportunities and emerging trends before competitors
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-4 mx-auto">
                <span className="text-2xl">🎯</span>
              </div>
              <h4 className="font-serif text-lg font-normal text-black mb-2">Competitor Intel</h4>
              <p className="font-sans text-sm text-gray-600">
                Monitor competitor activity and market movements in real-time
              </p>
            </div>
          </div>
        </div>

        {/* ROI Section */}
        <div className="mt-12 bg-gradient-to-r from-black to-gray-800 rounded-2xl p-8 text-white">
          <div className="text-center">
            <h3 className="font-serif text-2xl font-normal mb-4">Why Brands Choose SocialScoop</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
              <div>
                <div className="text-3xl font-bold mb-2">89%</div>
                <div className="text-sm text-gray-300">Faster crisis response time</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">3.2x</div>
                <div className="text-sm text-gray-300">Increase in trend identification</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">52%</div>
                <div className="text-sm text-gray-300">Reduction in reputation incidents</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="font-sans text-gray-600 mb-4">
            All plans include a 14-day free trial • No setup fees • Cancel anytime
          </p>
          <Button 
            onClick={() => handleGetStarted()}
            size="lg"
            className="font-sans bg-black hover:bg-gray-800 px-8 py-3 mr-4"
          >
            Start Free Trial
          </Button>
          <Button 
            onClick={() => handleGetStarted('demo')}
            variant="outline"
            size="lg"
            className="font-sans border-gray-300 hover:border-gray-400 px-8 py-3"
          >
            Book a Demo
          </Button>
        </div>
      </div>
    </section>
  );
}